import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLazyGetBirthdayNotificationsListQuery } from "../../services/dashboard";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import Pagination from "../../components/Panigation";
dayjs.extend(utc);

interface NotificationRecipient {
  _id: string;
  fullName: string;
  email: string;
  phoneNo: string;
  dateOfBirth: string;
  memberAccount: string;
  dialCode: string;
}

interface BirthdayNotification {
  _id: string;
  title: string;
  message: string;
  content: string;
  status: string;
  createdAt: string;
  broadcastReciever: NotificationRecipient[];
}

interface BirthdayNotificationsProps {
  startDate: string;
  endDate: string;
}

export default function BirthdayNotifications({
  startDate,
  endDate,
}: BirthdayNotificationsProps) {
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [notifications, setNotifications] = useState<BirthdayNotification[]>(
    []
  );

  const [selectedNotification, setSelectedNotification] =
    useState<BirthdayNotification | null>(null);
  const [isRecipientsModalOpen, setIsRecipientsModalOpen] = useState(false);

  const handleRecipientsClick = (notification: BirthdayNotification) => {
    setSelectedNotification(notification);
    setIsRecipientsModalOpen(true);
  };

  const [fetchBirthdayNotificationsListData] =
    useLazyGetBirthdayNotificationsListQuery();

  useEffect(() => {
    if (location.state.startDate && location.state.endDate) {
      loadBirthdayNotifications();
    }
  }, [location.state.startDate, location.state.endDate, page, pageSize]);

  const loadBirthdayNotifications = useCallback(async () => {
    try {
      const { data } = await fetchBirthdayNotificationsListData({
        type: "custom",
        startDate: location.state.startDate,
        endDate: location.state.endDate,
        page: page,
        limit: pageSize,
      }).unwrap();

      if (data?.notifications) {
        setNotifications(data.notifications);
        setTotalCount(data.total);
      }
    } catch (error) {
      console.error("Failed to fetch birthday notifications:", error);
    }
  }, [
    location.state.startDate,
    location.state.endDate,
    fetchBirthdayNotificationsListData,
    page,
    pageSize,
  ]);

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const totalPages = Math.max(
    1,
    Math.ceil((totalCount || 0) / (pageSize || 10))
  );

  const formatDate = (dateString: string) => {
    return dayjs.utc(dateString).format("DD-MM-YYYY, hh:mm A");
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { class: string; label: string }> = {
      sent: { class: "status-badge success", label: "Sent" },
      failed: { class: "status-badge error", label: "Failed" },
      scheduled: { class: "status-badge warning", label: "Scheduled" },
    };

    const statusInfo = statusMap[status.toLowerCase()] || {
      class: "status-badge",
      label: status,
    };

    return <span className={statusInfo.class}>{statusInfo.label}</span>;
  };

  return (
    <div className="birthday-notifications">
      <div className="notifications-header">
        <h2>Birthday Notifications</h2>
      </div>
      <div className="notifications-content">
        {notifications.length === 0 ? (
          <div className="no-data">No birthday notifications found</div>
        ) : (
          <div className="notifications-table-container">
            <TableContainer
              component="div"
              className="notifications-table-container"
            >
              <Table className="notifications-table" size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date Sent</TableCell>
                    <TableCell>Recipients</TableCell>
                    <TableCell>Content</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {notifications?.map((notification) => (
                    <TableRow key={notification._id} hover>
                      <TableCell className="notification-title">
                        {notification.title}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(notification.status)}
                      </TableCell>
                      <TableCell>
                        {formatDate(notification.createdAt)}
                      </TableCell>
                      <TableCell className="recipients-cell">
                        <div
                          className="recipients-count"
                          onClick={() => handleRecipientsClick(notification)}
                          style={{
                            cursor: "pointer",
                            color: "#0CD0C6",
                          }}
                        >
                          {notification.broadcastReciever?.length || 0}{" "}
                          recipients
                        </div>
                      </TableCell>
                      <TableCell
                        className="content-preview"
                        dangerouslySetInnerHTML={{
                          __html:
                            notification.content.length > 100
                              ? `${notification.content.substring(0, 100)}...`
                              : notification.content,
                        }}
                      />
                    </TableRow>
                  ))}
                  {notifications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No Data found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
        {notifications.length > 0 && (
          <Pagination
            module={notifications || []}
            page={page}
            setPage={setPage}
            limit={pageSize}
            setLimit={setPageSize}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        )}
        {/* Recipients Modal */}
        <Dialog
          open={isRecipientsModalOpen}
          onClose={() => setIsRecipientsModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Recipients</DialogTitle>
          <DialogContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Email</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedNotification?.broadcastReciever?.map(
                    (recipient, idx) => (
                      <TableRow key={`${recipient._id}-${idx}`}>
                        <TableCell>{recipient.fullName || "N/A"}</TableCell>
                        <TableCell>
                          {recipient.phoneNo
                            ? `${recipient.dialCode || ""} ${recipient.phoneNo}`
                            : "N/A"}
                        </TableCell>
                        <TableCell>{recipient.email || "N/A"}</TableCell>
                      </TableRow>
                    )
                  )}
                  {selectedNotification?.broadcastReciever?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        No recipients found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsRecipientsModalOpen(false)}
              color="primary"
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}
